/**
 * hydrus-waitlist-form.js
 *
 * Intercepts submit on .hydrus-inv-waitlist-form and routes the signup to
 * Klaviyo's client-side back-in-stock subscriptions endpoint when a public
 * API key is configured. On any Klaviyo failure (missing key, network error,
 * non-2xx response) the request silently falls back to a Shopify /contact
 * POST using the same form's action — the legacy path that Gorgias triage
 * still monitors. Hybrid fallback is the permanent default, not a rollout
 * safety net.
 *
 * UI is optimistic: the personalized thank-you swap fires as soon as submit
 * is clicked, independent of which backend accepted the signup. If both
 * paths fail the customer still sees confirmation — acceptable tradeoff
 * since (a) Klaviyo uptime is high, (b) /contact fallback is local infra,
 * (c) re-submit is allowed if they never hear back.
 *
 * Event delegation on document — handler survives DOM swaps from the
 * Section Rendering API reactive-zone replacement in hydrus-variant-reactivity.js.
 * A singleton guard (window.HydrusWaitlistForm) prevents duplicate listener
 * attachment if the script is re-injected after a section re-render.
 *
 * Klaviyo endpoint reference:
 *   POST https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=<KEY>
 *   https://developers.klaviyo.com/en/reference/create_client_back_in_stock_subscription
 *
 * Variant ID convention (set via Liquid data attribute):
 *   $shopify:::$default:::<shopify_variant_id>
 */
(function () {
  'use strict';
  if (window.HydrusWaitlistForm) return;
  window.HydrusWaitlistForm = true;

  var KLAVIYO_ENDPOINT = 'https://a.klaviyo.com/client/back-in-stock-subscriptions/';
  var KLAVIYO_API_REVISION = '2026-04-15';

  function submitToKlaviyo(companyId, variantId, email, phone) {
    var channels = phone ? ['EMAIL', 'SMS'] : ['EMAIL'];
    var profileAttributes = { email: email };
    if (phone) profileAttributes.phone_number = phone;

    var payload = {
      data: {
        type: 'back-in-stock-subscription',
        attributes: {
          profile: { data: { type: 'profile', attributes: profileAttributes } },
          channels: channels
        },
        relationships: {
          variant: { data: { type: 'catalog-variant', id: variantId } }
        }
      }
    };

    return fetch(KLAVIYO_ENDPOINT + '?company_id=' + encodeURIComponent(companyId), {
      method: 'POST',
      headers: {
        accept: 'application/json',
        'content-type': 'application/json',
        revision: KLAVIYO_API_REVISION
      },
      body: JSON.stringify(payload)
    }).then(function (res) {
      if (!res.ok) throw new Error('Klaviyo ' + res.status);
      return res;
    });
  }

  function submitToShopifyContact(form, formData) {
    return fetch(form.action, { method: 'POST', body: formData });
  }

  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form.matches || !form.matches('.hydrus-inv-waitlist-form')) return;
    e.preventDefault();

    var formData = new FormData(form);
    var firstName = String(formData.get('contact[first_name]') || '').trim() || 'friend';
    var email = String(formData.get('contact[email]') || '').trim();
    var smsOptIn = formData.get('contact[sms_consent]') === 'true';
    var phone = smsOptIn ? String(formData.get('contact[phone]') || '').trim() : '';
    var nextBatch = form.dataset.nextBatch || 'the next batch';
    var companyId = (form.dataset.klaviyoCompanyId || '').trim();
    var variantId = (form.dataset.klaviyoVariantId || '').trim();

    var klaviyoEligible = companyId && variantId && email;

    if (klaviyoEligible) {
      submitToKlaviyo(companyId, variantId, email, phone || null).catch(function (err) {
        if (window.console) console.warn('[HydrusWaitlist] Klaviyo failed, falling back to /contact:', err);
        submitToShopifyContact(form, formData).catch(function () { /* silent */ });
      });
    } else {
      submitToShopifyContact(form, formData).catch(function () { /* silent */ });
    }

    var container = form.closest('.hydrus-inv-badge.hydrus-inv-waitlist');
    if (!container) return;

    var safeName = String(firstName).replace(/[<>&"']/g, '');
    if (form.dataset.thanks) {
      // A27 back-in-stock variant: its own thank-you line, no batch language.
      var t = document.createElement('div');
      t.className = 'hydrus-inv-waitlist-thanks-line';
      t.textContent = form.dataset.thanks;
      container.innerHTML = '<div class="hydrus-inv-waitlist-label">You\'re on the list.</div>' +
        '<div class="hydrus-inv-waitlist-thanks"></div>';
      container.querySelector('.hydrus-inv-waitlist-thanks').appendChild(t);
      return;
    }

    container.innerHTML =
      '<div class="hydrus-inv-waitlist-label">You\'re on the list.</div>' +
      '<div class="hydrus-inv-waitlist-thanks">' +
        '<div class="hydrus-inv-waitlist-thanks-line">✓ You\'re on the list, ' + safeName + '.</div>' +
        '<div class="hydrus-inv-waitlist-thanks-line">We\'ll email you the moment Batch ' + nextBatch + ' opens.</div>' +
        '<div class="hydrus-inv-waitlist-thanks-sig">Jason</div>' +
      '</div>';
  });
})();
